'use client'

import { useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { isoWeek } from '@/lib/dates'
import { TOOLTIP_STYLE, AXIS_STYLE, CHART_COLORS } from './chartConfig'
import type { Task } from '@/types'

export default function WIPChart({ tasks }: { tasks: Task[] }) {
  const [mode, setMode] = useState<'tasks' | 'hours' | 'both'>('tasks')

  const data = useMemo(() => {
    const weeks: { label: string; date: Date }[] = []
    for (let i = 12; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i * 7)
      weeks.push({ label: isoWeek(d.toISOString().slice(0, 10)), date: d })
    }
    return weeks.map(w => {
      const open = tasks.filter(t => {
        const created = new Date(t.created)
        const closed = t.stage === 'Done' && t.last_stage ? new Date(t.last_stage) : null
        return created <= w.date && (!closed || closed > w.date) && t.stage !== 'Backlog'
      })
      return {
        label: w.label.slice(5),
        tasks: open.length,
        hours: Math.round(open.reduce((sum, t) => sum + (t.alloc_h || 0), 0)),
      }
    })
  }, [tasks])

  return (
    <div>
      <div className="flex gap-1 mb-3">
        {(['tasks', 'hours', 'both'] as const).map(m => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${mode === m ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'}`}>
            {m === 'tasks' ? 'Tasks' : m === 'hours' ? 'Est. Hours' : 'Both'}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data}>
          <XAxis dataKey="label" {...AXIS_STYLE} />
          <YAxis {...AXIS_STYLE} width={35} />
          <Tooltip {...TOOLTIP_STYLE} />
          {mode !== 'hours' && <Bar dataKey="tasks" fill={CHART_COLORS.primary} radius={[2, 2, 0, 0]} name="Tasks" />}
          {mode !== 'tasks' && <Bar dataKey="hours" fill={CHART_COLORS.primaryLight} radius={[2, 2, 0, 0]} name="Est. Hours" />}
          {mode === 'both' && <Legend wrapperStyle={{ fontSize: 12 }} />}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
